import { EventType } from "@/analytics/analytics-events";
import { StartEditWaypointsToolProperties } from "@/analytics/edit-waypoints-tool-properties";
import { useUiOverlayContext } from "@/components/common/ui-overlay-provider";
import {
  CONTAINER_WIDTH_PERCENTAGE,
  CONTAINER_WIDTH_PERCENTAGE_EDITING,
  CONTAINER_WIDTH_PERCENTAGE_HOVERED,
  useMinimapHeight,
} from "@/hooks/use-minimap-height";
import {
  selectIsMinimapEditing,
  setMiniMapIsEditing,
} from "@/store/minimap-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  ArrowDownIcon,
  FaroText,
  grey,
  useNonExhaustiveEffect,
} from "@faro-lotv/app-component-toolbox";
import { NO_TRANSLATE_CLASS } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Box, Stack } from "@mui/material";
import { useCallback, useMemo, useState, type JSX } from "react";
import { AppColorBar } from "../app-color-bar";
import { HIDE_FROM_SCREENSHOTS_CLASS } from "../hide-from-screenshots-class";
import { MiniMapEditWaypointsToolbar } from "./minimap-edit-waypoints-toolbar";
import {
  MinimapOverlayCanvas,
  MinimapOverlayCanvasProps,
} from "./minimap-overlay-canvas";

export type MiniMapOverlayProps = Pick<
  MinimapOverlayCanvasProps,
  "eventDivRef" | "canvasRef"
> & {
  /** Name of the sheet currently selected */
  activeSheetName?: string;

  /** Whether the minimap should be always minimized, for example because the picking tools are active */
  forceMinimized: boolean;
};

/** The length of the minimap size transition */
const TRANSITION_TIME = "0.5s";

/**
 * The minimum height for proper responsiveness. Below this threshold we stop caring about overlaps.
 * Mainly so landscape mobiles still can see important information, until we define a proper mobile view.
 * see https://faro01.atlassian.net/browse/SWEB-4848
 */
const MIN_HEIGHT_FOR_RESPONSIVENESS = 30;

/**
 * @returns An overlay with the minimap functions to use in pair with MiniMap
 */
export function MiniMapOverlay({
  eventDivRef,
  canvasRef,
  activeSheetName,
  forceMinimized,
}: MiniMapOverlayProps): JSX.Element {
  const dispatch = useAppDispatch();

  const [isMinimized, setIsMinimized] = useState(forceMinimized);
  const [isContainerHovered, setIsContainerHovered] = useState(false);
  const [isFloorPlanLabelHovered, setIsFloorPlanLabelHovered] = useState(false);
  const isEditing = useAppSelector(selectIsMinimapEditing);

  const { bottomRightAvailableHeight } = useUiOverlayContext();
  const maxHeight =
    bottomRightAvailableHeight !== undefined &&
    bottomRightAvailableHeight > MIN_HEIGHT_FOR_RESPONSIVENESS
      ? bottomRightAvailableHeight
      : undefined;

  // Using a state instead of a reference to be sure that the useEffects that depend
  // on this element are mounted properly
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  function handleToggleMinimized(): void {
    // If the user is measuring or annotating, clicks on the minimized minimap should not have effect
    if (forceMinimized) return;
    // Toggle the 'minimized' prop
    setIsMinimized(!isMinimized);
    if (!isMinimized) {
      setIsFloorPlanLabelHovered(false);
      setIsContainerHovered(false);
    }
  }

  // This hook ensures that the minimap is minimized if forcedMinimize is true
  // if forceMinimized is set to false again the previous status of the minimap is restored
  useNonExhaustiveEffect(() => {
    if (forceMinimized) {
      const wasMinimized = isMinimized;
      setIsMinimized(true);
      return () => {
        setIsMinimized(wasMinimized);
        if (!wasMinimized) {
          setIsFloorPlanLabelHovered(false);
          setIsContainerHovered(false);
        }
      };
    }
  }, [forceMinimized]);

  const widthPercentage = useMemo(() => {
    if (isEditing) return CONTAINER_WIDTH_PERCENTAGE_EDITING;
    if (isContainerHovered) return CONTAINER_WIDTH_PERCENTAGE_HOVERED;
    return CONTAINER_WIDTH_PERCENTAGE;
  }, [isEditing, isContainerHovered]);

  const canvasHeight = useMinimapHeight({
    container,
    isContainerHovered,
    isMinimized,
    isFloorPlanLabelHovered,
    widthPercentage,
  });

  const isAllowedToEditWaypoints = useAppSelector(selectHasWritePermission);

  const onToggleEditing = useCallback(() => {
    if (!isEditing) {
      Analytics.track<StartEditWaypointsToolProperties>(
        EventType.startEditWaypointsTool,
        { via: "minimap" },
      );
    }

    dispatch(setMiniMapIsEditing(!isEditing));
  }, [dispatch, isEditing]);

  return (
    <Box
      // This container is only used to get the value in pixels of 40% of the width
      ref={setContainer}
      component="div"
      className={HIDE_FROM_SCREENSHOTS_CLASS}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      <Stack
        aria-label="minimap overlay"
        sx={{
          alignItems: "stretch",
          // Place the minimap in the bottom right
          position: "absolute",
          right: 0,
          bottom: 0,
          // Add some margin so that it's not exactly at the corner
          margin: 1,
          outline: `1px solid ${grey[0]}33`,
          borderRadius: "4px",
          backgroundColor: grey[1000],
          padding: 0.5,
          // Make the minimap a bit bigger on hover
          width: `${widthPercentage}%`,
          maxHeight,
          transition: `width ${TRANSITION_TIME}, max-height ${TRANSITION_TIME}`,
          // If a tool like the createAnnotation is enabled, we do not allow to open, click or hover the minimap
          pointerEvents: forceMinimized ? "none" : "auto",
        }}
        onPointerEnter={() => setIsContainerHovered(true)}
        onPointerLeave={() => setIsContainerHovered(false)}
      >
        <Box
          component="div"
          sx={{
            height: canvasHeight,
            // Position is set to relative so that the children can be positioned absolutely but inside the parent
            position: "relative",
            overflow: "hidden",
            transition: `height ${TRANSITION_TIME}`,
          }}
        >
          <Stack
            direction="row"
            sx={{
              // Use position absolute so that the content can be shown over the parent's padding
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              gap: 0.5,
            }}
          >
            <MinimapOverlayCanvas
              eventDivRef={eventDivRef}
              canvasRef={canvasRef}
              isContainerHovered={isContainerHovered}
              isAllowedToEdit={isAllowedToEditWaypoints}
              isEditing={isEditing}
              onToggleEditing={onToggleEditing}
            />
            <AppColorBar />
          </Stack>
        </Box>

        {isEditing ? (
          <MiniMapEditWaypointsToolbar
            onStopEditing={() => dispatch(setMiniMapIsEditing(false))}
          />
        ) : (
          <Stack
            direction="row"
            aria-label="minimap open close"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              backdropFilter: "blur(1px)",
              // Add some padding to the text only if the minimap is minimized, so that the border remains 4px
              pt:
                isMinimized && !isContainerHovered && !isFloorPlanLabelHovered
                  ? 0
                  : 0.5,
              cursor: "pointer",
              transition: "padding 1s",
            }}
            onPointerEnter={() => setIsFloorPlanLabelHovered(!forceMinimized)}
            onPointerLeave={() => setIsFloorPlanLabelHovered(false)}
            onClick={handleToggleMinimized}
          >
            <FaroText
              variant="heading14"
              sx={{ textOverflow: "ellipsis" }}
              className={NO_TRANSLATE_CLASS}
              dark
              noWrap
            >
              {activeSheetName}
            </FaroText>
            <ArrowDownIcon
              sx={{
                color: grey[100],
                width: "16px",
                height: "16px",
                ...(isMinimized && { transform: "rotate(180deg)" }),
              }}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
